/**
 * Smart Cart Synchronization
 * Replaces the heavy rebuildCart() function with intelligent sync using minimal requests
 * 
 * Key optimizations:
 * 1. Only syncs when actually needed (avoids unnecessary page load rebuilds)
 * 2. Uses differential updates instead of clear + rebuild
 * 3. Leverages webhook-based synchronization when possible
 * 4. Falls back to minimal sync only when required
 */

class SmartCartSync {
  constructor() {
    this.isProcessing = false;
    this.syncQueue = [];
    this.lastSyncTimestamp = 0;
    this.syncDebounceMs = 2000; // Wait 2s before syncing to batch multiple changes
    
    this.initializeSync();
  }

  initializeSync() {
    // Handle page load sync - but only if really necessary
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.handlePageLoadSync());
    } else {
      this.handlePageLoadSync();
    }

    // Listen for specific sync triggers
    document.addEventListener('cart:sync:requested', (e) => this.handleSyncRequest(e.detail));
    document.addEventListener('cart:sync:force', (e) => this.handleForcedSync(e.detail));
    
    // Listen for draft order updates (webhook alternative)
    document.addEventListener('cart:draft-order:updated', (e) => this.handleDraftOrderUpdate(e.detail));
  }

  /**
   * Handle page load synchronization - but intelligently
   */
  async handlePageLoadSync() {
    // Check if smart sync has been disabled due to failures
    if (sessionStorage.getItem('smart_sync_disabled') === 'true') {
      console.log('🔄 Smart sync: Disabled due to previous failures, skipping');
      return;
    }
    
    const rebuildNeeded = document.querySelector('body').dataset.rebuildCart === 'true';
    
    if (!rebuildNeeded) {
      console.log('🔄 Smart sync: No rebuild needed on page load');
      return;
    }

    console.log('🔄 Smart sync: Page load synchronization needed');
    
    // Check if we can use webhook-based sync instead
    const lastWebhookUpdate = localStorage.getItem('last_webhook_cart_update');
    const pageLoadTime = Date.now();
    
    // If webhook updated cart within last 10 seconds, trust it and just refresh UI
    if (lastWebhookUpdate && (pageLoadTime - parseInt(lastWebhookUpdate)) < 10000) {
      console.log('🔄 Smart sync: Recent webhook update detected, skipping rebuild');
      await this.refreshCartDisplayOnly();
      this.markSyncComplete();
      return;
    }

    // Otherwise, do minimal synchronization
    await this.performMinimalSync();
  }

  /**
   * Perform minimal synchronization instead of full rebuild
   */
  async performMinimalSync() {
    // CRITICAL: Do NOT sync from backend during checkout/approval
    // Frontend cart changes are allowed, but backend rebuilds are blocked
    if (window.BLOCK_BACKEND_CART_REBUILDS) {
      console.warn('⛔ Smart sync BLOCKED - checkout in progress, backend rebuilds not allowed');
      return;
    }
    
    // Check if checkout is active (any flag)
    const checkoutStarted = localStorage.getItem('checkout_started');
    const checkoutProtection = localStorage.getItem('checkout_protection_active');
    const checkoutId = localStorage.getItem('checkoutId');
    
    if (checkoutStarted === 'true' || checkoutProtection === 'true' || checkoutId) {
      console.warn('⛔ Smart sync BLOCKED - checkout/approval in progress');
      console.log('🔄 Backend cart sync not allowed during checkout');
      return;
    }

    // Check if smart sync has been disabled due to failures
    if (sessionStorage.getItem('smart_sync_disabled') === 'true') {
      console.log('🔄 Smart sync: Disabled due to previous failures, skipping');
      return;
    }
    
    if (this.isProcessing) {
      console.log('🔄 Smart sync: Already processing, skipping');
      return;
    }

    this.isProcessing = true;
    console.log('🔄 Smart sync: Starting minimal synchronization...');

    try {
      // Show loading state
      this.showSyncIndicator();

      // Strategy 1: Try differential sync first (much faster)
      const syncSuccess = await this.tryDifferentialSync();
      
      if (!syncSuccess) {
        console.log('🔄 Smart sync: Differential sync failed, falling back to optimized rebuild');
        // Strategy 2: Fallback to optimized rebuild (still better than old method)
        await this.performOptimizedRebuild();
      }

      // Update cart display
      await this.refreshCartDisplayOnly();
      
      // Mark sync as complete
      this.markSyncComplete();
      
      console.log('✅ Smart sync: Synchronization completed');

    } catch (error) {
      console.error('❌ Smart sync: Synchronization failed:', error);
      // Ultimate fallback - only if everything else fails
      await this.performEmergencyFallback();
    } finally {
      this.isProcessing = false;
      this.hideSyncIndicator();
    }
  }

  /**
   * Try differential synchronization (fastest method)
   */
  async tryDifferentialSync() {
    try {
      // CRITICAL: Do NOT sync from backend during checkout/approval
      if (window.BLOCK_BACKEND_CART_REBUILDS) {
        console.warn('⛔ Smart sync BLOCKED - checkout in progress, backend rebuilds not allowed');
        return true; // Not an error, just blocked
      }
      
      // Check if checkout is active
      const checkoutStarted = localStorage.getItem('checkout_started');
      const checkoutProtection = localStorage.getItem('checkout_protection_active');
      const checkoutId = localStorage.getItem('checkoutId');
      
      if (checkoutStarted === 'true' || checkoutProtection === 'true' || checkoutId) {
        console.warn('⛔ Smart sync BLOCKED - checkout/approval in progress');
        console.log('🔄 Backend cart sync not allowed during checkout');
        return true; // Not an error, just blocked
      }

      console.log('🔄 Smart sync: Attempting differential sync...');

      // Get customer data
      const customerData = this.getCustomerData();
      if (!customerData) {
        console.log('🔄 Smart sync: No customer data available, skipping sync');
        return true; // Not an error, just no sync needed
      }

      // Use shared cart service's debounced wait if available
      if (window.sharedCartService && window.sharedCartService.waitForWebhookProcessing) {
        console.log('🔄 Smart sync: Using shared cart service debounced delay');
        await window.sharedCartService.waitForWebhookProcessing();
      } else {
        // Fallback to simple delay if shared cart service not available
        const webhookDelay = window.SHARED_CART_WEBHOOK_DELAY || 2000;
        console.log(`🔄 Smart sync: Waiting ${webhookDelay}ms for webhook to update draft order...`);
        await new Promise(resolve => setTimeout(resolve, webhookDelay));
      }

      // Get both current cart and backend draft order data in parallel
      const [currentCartResponse, backendItemsResponse] = await Promise.all([
        fetch('/cart.json', { 
          headers: window.getCartHeaders() 
        }),
        fetch(`/apps/stroetmann/api/v1/checkouts/shared/${customerData.userNumber}/items`, { 
          headers: window.getCartHeaders() 
        })
      ]);

      if (!currentCartResponse.ok) {
        throw new Error('Failed to fetch current cart data');
      }

      const currentCart = await currentCartResponse.json();
      
      // Check if backend has draft order data
      if (!backendItemsResponse.ok) {
        if (backendItemsResponse.status === 404) {
          console.log('🔄 Smart sync: No backend draft order found, skipping sync');
          return true; // Not an error, just no backend data
        }
        throw new Error('Failed to fetch backend cart data');
      }

      const backendItems = await backendItemsResponse.json();

      // If backend has no items and frontend has no items, we're in sync
      if (backendItems.length === 0 && currentCart.items.length === 0) {
        console.log('✅ Smart sync: Both carts are empty, no sync needed');
        return true;
      }

      // If backend has no items but frontend has items, we need to clear frontend
      if (backendItems.length === 0 && currentCart.items.length > 0) {
        console.log('🔄 Smart sync: Backend is empty, clearing frontend cart');
        await this.clearFrontendCart();
        return true;
      }

      // Calculate the differences
      const changes = this.calculateCartDifferences(currentCart.items, backendItems);
      
      console.log('🔄 Smart sync: Cart changes detected:', changes);

      // If no changes needed, we're done!
      if (changes.toAdd.length === 0 && changes.toUpdate.length === 0 && changes.toRemove.length === 0) {
        console.log('✅ Smart sync: No changes needed, carts are in sync');
        return true;
      }

      // Apply changes efficiently
      await this.applyCartChanges(changes);
      
      return true;

    } catch (error) {
      console.warn('⚠️ Smart sync: Differential sync failed:', error.message);
      return false;
    }
  }

  /**
   * Calculate differences between current and expected cart
   * FIXED: Improved deduplication logic to prevent item duplication issues
   */
  calculateCartDifferences(currentItems, expectedItems) {
    const changes = {
      toAdd: [],
      toUpdate: [],
      toRemove: []
    };

    // Create maps for easier comparison with improved key generation
    const currentItemsMap = new Map();
    const expectedItemsMap = new Map();
    
    // Process current items and handle duplicates by merging quantities
    currentItems.forEach(item => {
      const key = this.generateItemKey(item, 'frontend');
      if (currentItemsMap.has(key)) {
        // Merge quantities for duplicate items
        const existing = currentItemsMap.get(key);
        existing.quantity += item.quantity;
        console.warn(`⚠️ Smart sync: Merged duplicate current item ${key}, total quantity: ${existing.quantity}`);
      } else {
        currentItemsMap.set(key, { ...item });
      }
    });
    
    // Process expected items and handle duplicates by merging quantities
    expectedItems.forEach(item => {
      const key = this.generateItemKey(item, 'backend');
      if (expectedItemsMap.has(key)) {
        // Merge quantities for duplicate items
        const existing = expectedItemsMap.get(key);
        existing.quantity += item.quantity;
        console.warn(`⚠️ Smart sync: Merged duplicate expected item ${key}, total quantity: ${existing.quantity}`);
      } else {
        expectedItemsMap.set(key, { ...item });
      }
    });

    console.log(`🔍 Smart sync: Comparing ${currentItemsMap.size} current items vs ${expectedItemsMap.size} expected items`);

    // Find items to add or update
    expectedItemsMap.forEach((expectedItem, key) => {
      const currentItem = currentItemsMap.get(key);
      
      if (!currentItem) {
        // Item needs to be added
        changes.toAdd.push({
          id: expectedItem.id, // Backend uses 'id'
          quantity: expectedItem.quantity,
          properties: this.convertBackendProperties(expectedItem.properties)
        });
        console.log(`➕ Smart sync: Will add ${key} (qty: ${expectedItem.quantity})`);
      } else if (currentItem.quantity !== expectedItem.quantity) {
        // Item quantity needs to be updated
        changes.toUpdate.push({
          id: currentItem.variant_id, // Frontend uses 'variant_id'
          quantity: expectedItem.quantity,
          properties: this.convertBackendProperties(expectedItem.properties)
        });
        console.log(`🔄 Smart sync: Will update ${key} from ${currentItem.quantity} to ${expectedItem.quantity}`);
      } else {
        console.log(`✅ Smart sync: ${key} already matches (qty: ${currentItem.quantity})`);
      }
    });

    // Find items to remove
    currentItemsMap.forEach((currentItem, key) => {
      if (!expectedItemsMap.has(key)) {
        changes.toRemove.push({
          id: currentItem.variant_id, // Frontend uses 'variant_id'
          quantity: 0
        });
        console.log(`➖ Smart sync: Will remove ${key} (qty: ${currentItem.quantity})`);
      }
    });

    return changes;
  }

  /**
   * Generate consistent item key for comparison
   * Uses the same format as webhook-cart-detector for consistency
   */
  generateItemKey(item, source) {
    console.log(`🔧 Smart sync: Generating key for ${source} item:`, item);
    
    // Handle different data structures between frontend and backend
    let variantId;
    
    if (source === 'frontend') {
      // Frontend cart uses variant_id
      variantId = item.variant_id || item.id;
    } else {
      // Backend uses id (which is the variant ID)
      variantId = item.id;
    }
    
    console.log(`🔧 Smart sync: Variant ID for ${source}: ${variantId}`);
    
    // Include properties in the key for items with custom properties
    let properties = {};
    
    if (source === 'frontend') {
      // Frontend properties are already in object format
      properties = item.properties || {};
      console.log(`🔧 Smart sync: Frontend properties:`, properties);
    } else {
      // Backend properties are in array format, convert to object
      properties = this.convertBackendProperties(item.properties);
      console.log(`🔧 Smart sync: Backend properties after conversion:`, properties);
    }
    
    // Use the same format as webhook-cart-detector
    if (Object.keys(properties).length > 0) {
      const propertiesString = Object.entries(properties)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}:${value}`)
        .join('|');
      const key = `${variantId}:${propertiesString}`;
      console.log(`🔧 Smart sync: Generated key for ${source}: ${key}`);
      return key;
    }
    
    const key = variantId;
    console.log(`🔧 Smart sync: Generated key for ${source}: ${key}`);
    return key;
  }

  /**
   * Convert backend properties array to object format
   */
  convertBackendProperties(backendProperties) {
    console.log('🔧 Smart sync: Converting backend properties:', backendProperties);
    
    if (!backendProperties || !Array.isArray(backendProperties)) {
      console.log('🔧 Smart sync: No backend properties or not array, returning empty object');
      return {};
    }
    
    const propertiesObj = {};
    backendProperties.forEach(prop => {
      if (prop.key && prop.value !== undefined) {
        propertiesObj[prop.key] = prop.value;
        console.log(`🔧 Smart sync: Converted property ${prop.key}: ${prop.value}`);
      }
    });
    
    console.log('🔧 Smart sync: Converted properties object:', propertiesObj);
    return propertiesObj;
  }

  /**
   * Apply calculated changes to cart using webhook-safe operations
   * FIXED: Now uses webhook-safe headers instead of bypassing webhooks
   */
  async applyCartChanges(changes) {
    const promises = [];

    // Remove items (set quantity to 0)
    if (changes.toRemove.length > 0) {
      console.log('🔄 Smart sync: Removing items (webhook-safe):', changes.toRemove);
      promises.push(
        fetch('/cart/update.js', {
          method: 'POST',
          headers: window.createWebhookSafeHeaders ? 
            window.createWebhookSafeHeaders() : 
            window.getCartHeaders(),
          body: JSON.stringify({
            updates: changes.toRemove.reduce((acc, item) => {
              acc[item.id] = 0;
              return acc;
            }, {})
          })
        })
      );
    }

    // Update existing items
    if (changes.toUpdate.length > 0) {
      console.log('🔄 Smart sync: Updating items (webhook-safe):', changes.toUpdate);
      for (const item of changes.toUpdate) {
        promises.push(
          fetch('/cart/change.js', {
            method: 'POST',
            headers: window.createWebhookSafeHeaders ? 
              window.createWebhookSafeHeaders() : 
              window.getCartHeaders(),
            body: JSON.stringify({
              id: item.id,
              quantity: item.quantity,
              properties: item.properties
            })
          })
        );
      }
    }

    // Add new items
    if (changes.toAdd.length > 0) {
      console.log('🔄 Smart sync: Adding items (webhook-safe):', changes.toAdd);
      promises.push(
        fetch('/cart/add.js', {
          method: 'POST',
          headers: window.createWebhookSafeHeaders ? 
            window.createWebhookSafeHeaders() : 
            window.getCartHeaders(),
          body: JSON.stringify({
            items: changes.toAdd
          })
        })
      );
    }

    // Execute all changes in parallel for speed
    const results = await Promise.allSettled(promises);
    
    // Check if any changes failed
    const failures = results.filter(result => result.status === 'rejected');
    if (failures.length > 0) {
      console.warn('⚠️ Smart sync: Some cart changes failed:', failures);
      throw new Error(`${failures.length} cart changes failed`);
    }

    console.log('✅ Smart sync: All cart changes applied successfully (webhook-safe)');
  }

  /**
   * Perform optimized rebuild using available rebuild functions
   */
  async performOptimizedRebuild() {
    console.log('🔄 Smart sync: Performing optimized rebuild...');
    
    if (typeof window.rebuildCartOptimized === 'function') {
      // Use the smart rebuild function if available
      console.log('✅ Smart sync: Using rebuildCartOptimized function');
      await window.rebuildCartOptimized();
    } else if (typeof window.rebuildCart === 'function') {
      // Last resort - use old rebuild function
      console.warn('⚠️ Smart sync: Using legacy rebuildCart function');
      await window.rebuildCart();
    } else {
      // No rebuild functions available - use section rendering as fallback
      console.warn('⚠️ Smart sync: No rebuild functions available, using section rendering fallback');
      await this.refreshCartDisplayOnly();
    }
  }

  /**
   * Emergency fallback - disable smart sync and use legacy methods
   * FIXED: No longer causes page refresh to prevent endless reloading
   */
  async performEmergencyFallback() {
    console.error('🚨 Smart sync: All sync methods failed, disabling smart sync');
    
    // Set a flag to prevent infinite refresh loops
    const failureCount = parseInt(sessionStorage.getItem('cart_sync_failure_count') || '0');
    if (failureCount >= 3) {
      console.error('🚨 Smart sync: Too many failures, permanently disabling smart sync for this session');
      sessionStorage.setItem('smart_sync_disabled', 'true');
      return;
    }
    
    sessionStorage.setItem('cart_sync_failure_count', (failureCount + 1).toString());
    
    // Instead of refreshing, mark sync as failed and let the page continue
    this.markSyncComplete();
    
    // Show user a gentle error message instead of forcing refresh
    const event = new CustomEvent('cart:sync:failed', {
      detail: { 
        message: 'Cart synchronization encountered an issue. Please refresh the page if cart appears outdated.',
        attempts: failureCount + 1
      }
    });
    document.dispatchEvent(event);
  }

  /**
   * Refresh cart display elements only (no API calls)
   */
  async refreshCartDisplayOnly() {
    try {
      // Update cart sections via section rendering
      const response = await fetch(`${window.theme.routes.cart}?sections=cart-icon-bubble,cart-drawer`, {
        headers: window.getCartHeaders()
      });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Update cart icon bubble
        const cartBubble = doc.querySelector('#cart-icon-bubble');
        if (cartBubble) {
          const currentBubble = document.querySelector('#cart-icon-bubble');
          if (currentBubble) {
            currentBubble.outerHTML = cartBubble.outerHTML;
          }
        }
        
        console.log('✅ Smart sync: Cart display refreshed');
      }
    } catch (error) {
      console.warn('⚠️ Smart sync: Failed to refresh cart display:', error);
    }
  }

  /**
   * Handle webhook-triggered draft order updates
   */
  async handleDraftOrderUpdate(detail) {
    console.log('🔄 Smart sync: Draft order updated via webhook:', detail);
    
    // Store timestamp for future page load optimizations
    localStorage.setItem('last_webhook_cart_update', Date.now().toString());
    
    // Refresh cart display
    this.refreshCartDisplayOnly();
  }

  /**
   * Mark synchronization as complete
   */
  markSyncComplete() {
    // Update body dataset
    document.querySelector('body').dataset.rebuildCart = 'false';
    window.rebuild = false;
    
    // Clear all failure counters on successful sync
    sessionStorage.removeItem('cart_sync_refresh_count');
    sessionStorage.removeItem('cart_sync_failure_count');
    sessionStorage.removeItem('user_number_reload_count');
    
    // Only remove disabled flags if sync was successful
    if (!sessionStorage.getItem('cart_sync_failure_count')) {
      sessionStorage.removeItem('smart_sync_disabled');
    }
    
    // Dispatch completion event
    document.dispatchEvent(new CustomEvent('cart:sync:completed', {
      detail: { timestamp: Date.now() }
    }));
  }

  /**
   * Show sync indicator
   */
  showSyncIndicator() {
    const cartBubble = document.getElementById('cart-icon-bubble');
    const loadingIndicator = document.querySelector('.cart-icon-bubble-loading');
    
    if (cartBubble) cartBubble.classList.add('loading');
    if (loadingIndicator) loadingIndicator.classList.remove('hidden');
  }

  /**
   * Hide sync indicator  
   */
  hideSyncIndicator() {
    const cartBubble = document.getElementById('cart-icon-bubble');
    const loadingIndicator = document.querySelector('.cart-icon-bubble-loading');
    
    if (cartBubble) cartBubble.classList.remove('loading');
    if (loadingIndicator) loadingIndicator.classList.add('hidden');
  }

  /**
   * Handle sync request from other components
   */
  async handleSyncRequest(detail) {
    console.log('🔄 Smart sync: Sync requested:', detail);
    await this.performMinimalSync();
  }

  /**
   * Handle forced sync (bypass optimizations)
   */
  async handleForcedSync(detail) {
    console.log('🔄 Smart sync: Forced sync requested:', detail);
    await this.performOptimizedRebuild();
    await this.refreshCartDisplayOnly();
    this.markSyncComplete();
  }

  /**
   * Get customer data for API calls
   */
  getCustomerData() {
    if (!window.customer || !window.customer.id) {
      return null;
    }
    
    return {
      customerId: window.customer.id,
      userName: window.customer.userName,
      userNumber: window.customer.currentUserNumber
    };
  }

  /**
   * Clear the frontend cart
   */
  async clearFrontendCart() {
    try {
      console.log('🗑️ Smart sync: Clearing frontend cart...');
      
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to clear cart: ${response.status}`);
      }

      console.log('✅ Smart sync: Frontend cart cleared successfully');
      
    } catch (error) {
      console.error('❌ Smart sync: Error clearing frontend cart:', error);
      throw error;
    }
  }
}

// // Initialize smart cart sync
// window.smartCartSync = new SmartCartSync();

// // Export for use by other components
// window.requestCartSync = (reason = 'manual') => {
//   document.dispatchEvent(new CustomEvent('cart:sync:requested', {
//     detail: { reason, timestamp: Date.now() }
//   }));
// };

// window.forceCartSync = (reason = 'forced') => {
//   document.dispatchEvent(new CustomEvent('cart:sync:force', {
//     detail: { reason, timestamp: Date.now() }
//   }));
// };

// console.log('✅ Smart Cart Sync initialized');